import csvDownload from 'json-to-csv-export';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getInvestorReport, getUserReport } from 'src/api/stoApi';
import { ConfirmDownload } from 'src/components/dialogs/ConfirmDownload';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { useSPVs } from 'src/hooks/useSPVs';
import { IInvestorReport } from 'src/models';
import { Routes } from 'src/router/Routes';
import { determineWhatTaxType } from 'src/util/taxes';
import { awaitUtil } from 'src/util/utils';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { Box, Button, Card, Chip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export const IssuerOverview = () => {
  const { t } = useTranslation();
  const spvArray = useSPVs();
  const [investorReportDialog, setInvestorReportDialog] = useState(false);
  const [userReportDialog, setUserReportDialog] = useState(false);
  const [investorReportSPV, setInvestorReportSPV] = useState<string>('');
  const navigate = useNavigate();

  const issuerHeaderOrder: Array<{ key: keyof IInvestorReport; value: string }> = [
    { key: 'userId', value: t('issuer.report.csv.userId') },
    { key: 'firstName', value: t('issuer.report.csv.firstName') },
    { key: 'lastName', value: t('issuer.report.csv.lastName') },
    { key: 'birthDate', value: t('issuer.report.csv.birthDate') },
    { key: 'street', value: t('issuer.report.csv.street') },
    { key: 'city', value: t('issuer.report.csv.city') },
    { key: 'postCode', value: t('issuer.report.csv.zipCode') },
    { key: 'countryCode', value: t('issuer.report.csv.country') },
    { key: 'email', value: t('issuer.report.csv.email') },
    { key: 'phone', value: t('issuer.report.csv.phone') },
    { key: 'assetTitle', value: t('issuer.report.csv.assetTitle') },
    { key: 'ISIN', value: t('issuer.report.csv.isin') },
    { key: 'numberOfShares', value: t('issuer.report.csv.amountOfShares') },
    { key: 'IBAN', value: t('issuer.report.csv.iban') },
    { key: 'financialAdvisor', value: t('issuer.report.csv.financialAdvisor') },
    { key: 'affiliatePartner', value: t('issuer.report.csv.affiliatePartner') },
    { key: 'subPartner', value: t('issuer.report.csv.subPartner') },
    { key: 'partnerId', value: t('issuer.report.csv.info') },
    { key: 'MarketingMessages', value: t('issuer.report.csv.MarketingMessages') },
    { key: 'MarketingMessagesDate', value: t('issuer.report.csv.MarketingMessagesDate') },
    { key: 'TelephoneMarketing', value: t('issuer.report.csv.TelephoneMarketing') },
    { key: 'TelephoneMarketingDate', value: t('issuer.report.csv.TelephoneMarketingDate') },
    { key: 'ProfilingMarketing', value: t('issuer.report.csv.ProfilingMarketing') },
    { key: 'ProfilingMarketingDate', value: t('issuer.report.csv.ProfilingMarketingDate') },
    { key: 'DataTransferMarketing', value: t('issuer.report.csv.DataTransferMarketing') },
    { key: 'DataTransferMarketingDate', value: t('issuer.report.csv.DataTransferMarketingDate') },
    { key: 'DataTransferMarketingLegalEntity', value: t('issuer.report.csv.DataTransferMarketingLegalEntity') },
    { key: 'generatedTimestamp', value: t('issuer.report.csv.generatedTimestamp') },
  ];

  const openIssuerReportDialog = (spvId: string) => {
    setInvestorReportSPV(spvId);
    setInvestorReportDialog(true);
  };
  const downloadIssuerReport = async () => {
    const [data, error] = await awaitUtil(getInvestorReport(investorReportSPV));
    if (error) return;
    if (!data) return;
    const reportArray = data.data.data.data;
    const csvdata = [
      issuerHeaderOrder.map((item) => item.value),
      ...reportArray.map((item) => {
        const array: any = [];
        issuerHeaderOrder.forEach((header) => {
          array.push(item[header.key]);
        });
        return array;
      }),
    ];
    // download array as csv file
    downloadArrayAsCSV(csvdata, 'Investierte User');
    // csvDownload(dataToConvert);
  };
  const downloadUserReport = async () => {
    const [data, error] = await awaitUtil(getUserReport());
    if (error) return;
    if (!data) return;
    const reportArray = data.data.data.data.map((item) => ({ ...item, financialAdvisor: item.financialAdvisor || '' }));
    const dataToConvert = {
      data: reportArray,
      filename: 'Registrierte user lt AV-Vertrag',
      delimiter: ';',
      headers: [
        t('issuer.report.csv.userId'),
        t('issuer.report.csv.firstName'),
        t('issuer.report.csv.lastName'),
        t('issuer.report.csv.street'),
        t('issuer.report.csv.zipCode'),
        t('issuer.report.csv.city'),
        t('issuer.report.csv.country'),
        t('issuer.report.csv.email'),
        t('issuer.report.csv.marketingConsent'),
        t('issuer.report.csv.status'),
        t('issuer.report.csv.verifiedTimestamp'),
        t('issuer.report.csv.activatedTimestamp'),
        t('issuer.report.csv.assetTitle'),
        t('issuer.report.csv.isin'),
        t('issuer.report.csv.numberOfShares'),
        t('issuer.report.csv.affiliatePartner'),
        t('issuer.report.csv.subPartner'),
        t('issuer.report.csv.generatedTimestamp'),
        t('issuer.report.csv.referredBy'),
        t('issuer.report.csv.registeredTimestamp'),
        t('issuer.report.csv.personalInformationTimestamp'),
        t('issuer.report.csv.documentsProvidedTimestamp'),
        t('issuer.report.csv.securityAppConnectedTimestamp'),
        t('issuer.report.csv.financialAdvisor'),
      ],
    };
    csvDownload(dataToConvert);
  };

  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.ISSUER.ROOT,
            label: t('issuer.overview.title'),
            icon: <PeopleAltIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
        ]}
        headline={t('issuer.overview.title')}
      >
        <Box>
          <Button
            variant="contained"
            disableElevation
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => setUserReportDialog(true)}
            sx={{ mr: 2 }}
          >
            {t('issuer.overview.button.downloadUserReport')}
          </Button>
          <Button
            variant="contained"
            disableElevation
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate(Routes.ISSUER.CREATE)}
          >
            {t('issuer.spv.createTitle')}
          </Button>
        </Box>
      </SiteHeader>
      <Card sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 500, mb: 3 }}>
          {t('issuer.overview.accounts')}
        </Typography>
        {spvArray &&
          spvArray.map((spv) => (
            <Grid container spacing={2} sx={{ pl: 2 }} key={spv.id}>
              <Grid xs={4}>
                <Typography variant="body1" sx={{ fontWeight: 500 }} data-testid="spv-name">
                  {spv.companyName}
                </Typography>
                <Typography variant="body1" sx={{ color: 'GrayText' }}>
                  {spv.taxNumber ? t('issuer.overview.taxId') + ' ' + spv.taxNumber : t('issuer.overview.noTaxId')}
                </Typography>
                {spv.mangopayReadiness?.isReady ? (
                  <Chip icon={<CheckCircleIcon />} label={t('payment.overview.sca.ready')} color="success" size="small" sx={{ mt: 0.5 }} />
                ) : (
                  <Chip
                    icon={<CancelIcon />}
                    label={t('payment.overview.sca.actionRequired')}
                    color="warning"
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Grid>
              <Grid xs={8} container justifyContent={'flex-start'}>
                <Grid>
                  <Button variant="contained" to={Routes.ISSUER.PROFILE.replace(':spvId', spv.id)} component={Link}>
                    {t('issuer.overview.button.issuerProfile')}
                  </Button>
                </Grid>
                <Grid>
                  <Button variant="contained" onClick={() => openIssuerReportDialog(spv.id)} startIcon={<DownloadIcon />}>
                    {t('issuer.overview.button.issuerReport')}
                  </Button>
                </Grid>
                <Grid>
                  <Button variant="contained" to={Routes.ISSUER.TAX.replace(':spvId', spv.id)} component={Link}>
                    {t('issuer.overview.button.taxInformation')} ({determineWhatTaxType(spv)})
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          ))}
      </Card>
      <ConfirmDownload
        open={investorReportDialog}
        setClose={setInvestorReportDialog}
        title={t('issuer.legalDialog.investorReport.title')}
        disclaimer={t('issuer.legalDialog.investorReport.body')}
        downloadButton={downloadIssuerReport}
      />
      <ConfirmDownload
        open={userReportDialog}
        setClose={setUserReportDialog}
        title={t('issuer.legalDialog.userReport.title')}
        disclaimer={t('issuer.legalDialog.userReport.body')}
        downloadButton={downloadUserReport}
      />
    </>
  );
};
function downloadArrayAsCSV(csvdata: any[], fileName: string) {
  const csvContent = 'data:text/csv;charset=utf-8,' + csvdata.map((e) => e.join(';')).join('\n');
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', fileName + '.csv');
  document.body.appendChild(link); // Required for FF
  link.click();
}
